/**
 * @Relay no-runtime
 */
/**
 * @graphql cc
 */
import graphql from 'graphql-tag';
import { useEffect } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { getTrialDaysLeftFromTrialEndDate } from '@atlassian/upgrades-and-purchases/get-trial-days-left';

import { fg } from '@confluence/feature-gating';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';

import type { usePublicLinkDisableTrialDetailsQuery as usePublicLinkDisableTrialDetailsQuery$data } from './__types__/usePublicLinkDisableTrialDetailsQuery';

type PublicLinkDisable = {
	shouldRenderDisablePublicLinkFeature: boolean;
};

type PublicLinkDisableParams = {
	source?: string;
};

export const DISABLE_PUBLIC_LINKS_ON_TRIAL_GATE = 'confluence_disable_public_links_on_trial';

export const usePublicLinkDisable = ({ source }: PublicLinkDisableParams): PublicLinkDisable => {
	const { isLoggedIn, userId, edition } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isFreeEdition = edition === ConfluenceEdition.FREE;

	const skipTrialDetailsQuery = isFreeEdition || !isLoggedIn || !userId;

	const {
		data: trialDetailsData,
		error: trialDetailsError,
		loading: trialDetailsLoading,
	} = useQuery<usePublicLinkDisableTrialDetailsQuery$data>(
		graphql`
			query usePublicLinkDisableTrialDetailsQuery {
				license {
					trialEndDate
				}
			}
		`,
		{
			skip: skipTrialDetailsQuery,
		},
	);

	const daysLeft =
		!trialDetailsError && !trialDetailsLoading
			? (getTrialDaysLeftFromTrialEndDate(trialDetailsData?.license?.trialEndDate) ?? 0)
			: 0;

	const isTrialEdition = daysLeft > 0;
	const shouldRenderDisablePublicLinkFeature =
		(isTrialEdition || isFreeEdition) && fg('confluence_disable_public_links_on_trial');

	useEffect(() => {
		if (source && (isFreeEdition || isTrialEdition)) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					actionSubject: 'feature',
					action: 'exposed',
					source,
					attributes: {
						flagKey: DISABLE_PUBLIC_LINKS_ON_TRIAL_GATE,
						flagValue: fg('confluence_disable_public_links_on_trial'),
						touchpointId: source,
						edition,
					},
				},
			}).fire();
		}
	}, [
		createAnalyticsEvent,
		edition,
		isFreeEdition,
		isTrialEdition,
		shouldRenderDisablePublicLinkFeature,
		source,
	]);

	return { shouldRenderDisablePublicLinkFeature };
};
